import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X, ArrowRight, ArrowLeft, Check, Loader2, MessageCircle, Mail, CheckCircle2 } from "lucide-react";
import { brochureOptions, enquiryOptions, formOptions, contact, social } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const STEPS = ["What you need", "Specs & market", "Your details"];

const empty = {
  interested_in: [],
  quantity: "",
  timeline: "",
  specs: "",
  country: "",
  business_type: "",
  company: "",
  person: "",
  email: "",
  mobile: "",
};

function composeMessage(f) {
  return [
    "Manufacturing enquiry — K.S.E. Inc.",
    `Products: ${f.interested_in.join(", ") || "-"}`,
    `Quantity: ${f.quantity || "-"}`,
    `Timeline: ${f.timeline || "-"}`,
    `Market: ${f.country || "-"}`,
    `Business: ${f.business_type || "-"}`,
    f.specs ? `Specs: ${f.specs}` : null,
    "",
    `${f.person || ""}${f.company ? " · " + f.company : ""}`,
    f.email || "",
    f.mobile || "",
  ].filter((l) => l !== null).join("\n");
}

export default function EnquiryWizard({ prefill, onClose }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...empty, ...(prefill || {}) });
  const [status, setStatus] = useState("idle"); // idle | sending | done | failed
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (item) =>
    set("interested_in", form.interested_in.includes(item) ? form.interested_in.filter((x) => x !== item) : [...form.interested_in, item]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const canNext = useMemo(() => {
    if (step === 0) return form.interested_in.length > 0 && form.quantity && form.timeline;
    if (step === 1) return form.country && form.business_type;
    return form.company && form.person && form.email && form.mobile;
  }, [step, form]);

  const message = composeMessage(form);
  const waHref = `https://wa.me/${contact.people[0]?.whatsapp || social.whatsapp}?text=${encodeURIComponent(message)}`;
  const mailHref = `mailto:${contact.people[0]?.email || ""}?subject=${encodeURIComponent("Manufacturing enquiry — " + (form.company || ""))}&body=${encodeURIComponent(message)}`;

  const submit = async () => {
    if (!canNext) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/leads`, {
        company: form.company,
        person: form.person,
        mobile: form.mobile,
        email: form.email,
        country: form.country,
        business_type: form.business_type,
        interested_in: form.interested_in,
        quantity: form.quantity,
        remark: `Timeline: ${form.timeline}${form.specs ? " | Specs: " + form.specs : ""}`,
        source: "manufacturing_enquiry",
      });
      setStatus("done");
    } catch (e) {
      // No backend (static hosting) or a network hiccup: hand the composed
      // enquiry to WhatsApp / email instead of losing it.
      setStatus("failed");
    }
  };

  const Chip = ({ on, children, onClick }) => (
    <button type="button" onClick={onClick} className={`kse-chip ${on ? "is-on" : ""}`}>
      {on && <Check size={14} className="inline mr-1 -mt-0.5" />}
      {children}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-label="Start a manufacturing enquiry">
      <div className="absolute inset-0 bg-[#0d1640]/75 backdrop-blur-sm" onClick={onClose} />
      <div className="kse-modal-in relative bg-white w-full sm:max-w-2xl max-h-[94vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl shadow-2xl">
        {/* header */}
        <div className="sticky top-0 z-10 bg-[#111c4e] text-white px-6 sm:px-8 py-5 rounded-t-3xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] tracking-[0.24em] uppercase text-blue-200 font-bold">Manufacturing enquiry</p>
              <h3 className="text-xl sm:text-2xl font-extrabold mt-1">
                {status === "done" ? "Received — thank you." : status === "failed" ? "Send it directly" : STEPS[step]}
              </h3>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white mt-1" aria-label="Close"><X size={24} /></button>
          </div>
          {status === "idle" || status === "sending" ? (
            <ol className="mt-4 flex items-center gap-2">
              {STEPS.map((s, i) => (
                <li key={s} className="flex items-center gap-2 text-xs">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${i < step ? "bg-green-400 text-[#0d1640]" : i === step ? "bg-white text-[#111c4e]" : "bg-white/15 text-white/70"}`}>
                    {i < step ? <Check size={13} /> : i + 1}
                  </span>
                  <span className={`hidden sm:inline ${i === step ? "text-white" : "text-white/60"}`}>{s}</span>
                  {i < STEPS.length - 1 && <span className="w-6 sm:w-10 h-px bg-white/25" />}
                </li>
              ))}
            </ol>
          ) : null}
        </div>

        <div className="px-6 sm:px-8 py-7">
          {status === "done" && (
            <div className="text-center py-6">
              <CheckCircle2 className="text-green-500 mx-auto mb-4" size={56} />
              <p className="text-slate-600 max-w-md mx-auto">
                Your enquiry is with {contact.people[1]?.name || "our team"}. Expect a reply within one working day — sooner on WhatsApp.
              </p>
              <div className="flex flex-wrap justify-center gap-3 mt-7">
                <a href={waHref} target="_blank" rel="noopener noreferrer" className="kse-btn bg-[#25D366] text-white"><MessageCircle size={18} /> Continue on WhatsApp</a>
                <button onClick={onClose} className="kse-btn border border-slate-200 text-[#1a2a6b]">Back to site</button>
              </div>
            </div>
          )}

          {status === "failed" && (
            <div className="py-2">
              <p className="text-slate-600">
                Our enquiry inbox is offline right now. Your enquiry is composed below — send it straight to
                {" "}{contact.people[0]?.name || "our team"} on WhatsApp or by email, nothing is lost.
              </p>
              <pre className="mt-5 whitespace-pre-wrap text-[13px] leading-relaxed bg-slate-50 border border-slate-200 rounded-2xl p-4 text-slate-700">{message}</pre>
              <div className="flex flex-wrap gap-3 mt-6">
                <a href={waHref} target="_blank" rel="noopener noreferrer" className="kse-btn bg-[#25D366] text-white"><MessageCircle size={18} /> Send on WhatsApp</a>
                <a href={mailHref} className="kse-btn bg-[#1a2a6b] text-white"><Mail size={18} /> Send by email</a>
                <button onClick={() => setStatus("idle")} className="kse-btn border border-slate-200 text-[#1a2a6b]">Edit</button>
              </div>
            </div>
          )}

          {(status === "idle" || status === "sending") && (
            <>
              {step === 0 && (
                <div className="space-y-7">
                  <div>
                    <p className="font-semibold text-[#0f1a3d] mb-3">What are you looking to produce? <span className="text-[#c0161c]">*</span></p>
                    <div className="flex flex-wrap gap-2">
                      {brochureOptions.interests.map((it) => (
                        <Chip key={it} on={form.interested_in.includes(it)} onClick={() => toggle(it)}>{it}</Chip>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0f1a3d] mb-3">Order quantity per style <span className="text-[#c0161c]">*</span></p>
                    <div className="flex flex-wrap gap-2">
                      {enquiryOptions.quantities.map((q) => (
                        <Chip key={q} on={form.quantity === q} onClick={() => set("quantity", q)}>{q}</Chip>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">Our floor runs 4,00,000 knit and 80,000 shirt pieces a month — no order is too big.</p>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0f1a3d] mb-3">When do you need it? <span className="text-[#c0161c]">*</span></p>
                    <div className="flex flex-wrap gap-2">
                      {enquiryOptions.timelines.map((t) => (
                        <Chip key={t} on={form.timeline === t} onClick={() => set("timeline", t)}>{t}</Chip>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="font-semibold text-[#0f1a3d] mb-2 block">Target market <span className="text-[#c0161c]">*</span></label>
                      <select className="kse-input" value={form.country} onChange={(e) => set("country", e.target.value)}>
                        <option value="">Select a country</option>
                        {formOptions.countries.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="font-semibold text-[#0f1a3d] mb-2 block">You are a <span className="text-[#c0161c]">*</span></label>
                      <select className="kse-input" value={form.business_type} onChange={(e) => set("business_type", e.target.value)}>
                        <option value="">Select business type</option>
                        {formOptions.businessTypes.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="font-semibold text-[#0f1a3d] mb-2 block">Fabric, sizes, prints, packing, target price — anything that helps us quote</label>
                    <textarea className="kse-input min-h-[120px]" placeholder="e.g. 180 gsm cotton single jersey, S–XXL, 4-colour print, folded & poly-bagged, FOB Mumbai" value={form.specs} onChange={(e) => set("specs", e.target.value)} />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="grid sm:grid-cols-2 gap-5">
                  {[
                    ["company", "Company", "text", "Brand or buying house"],
                    ["person", "Your name", "text", "Full name"],
                    ["email", "Work email", "email", "name@company.com"],
                    ["mobile", "Mobile / WhatsApp", "tel", "+44 …"],
                  ].map(([k, label, type, ph]) => (
                    <div key={k}>
                      <label className="font-semibold text-[#0f1a3d] mb-2 block">{label} <span className="text-[#c0161c]">*</span></label>
                      <input className="kse-input" type={type} placeholder={ph} value={form[k]} onChange={(e) => set(k, e.target.value)} />
                    </div>
                  ))}
                  <p className="sm:col-span-2 text-xs text-slate-500">
                    We reply within one working day. Your details go only to the K.S.E. Inc. team.
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between gap-3 mt-8 pt-6 border-t border-slate-100">
                <button type="button" onClick={() => (step === 0 ? onClose() : setStep(step - 1))} className="kse-btn border border-slate-200 text-[#1a2a6b]">
                  <ArrowLeft size={16} /> {step === 0 ? "Cancel" : "Back"}
                </button>
                {step < 2 ? (
                  <button type="button" disabled={!canNext} onClick={() => setStep(step + 1)} className="kse-btn kse-btn--accent disabled:opacity-40 disabled:cursor-not-allowed">
                    Continue <ArrowRight size={16} />
                  </button>
                ) : (
                  <button type="button" disabled={!canNext || status === "sending"} onClick={submit} className="kse-btn kse-btn--accent disabled:opacity-40 disabled:cursor-not-allowed">
                    {status === "sending" ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Send enquiry
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
